import { createReducer, on } from '@ngrx/store';

import { LastMessage, ObjectCardKind } from '@common/components/object-card';
import { ROUND_COORDINATE } from '@common/utils/consts/constants';
import {
  addMonitoringItem,
  addMonitoringItems,
  batchSyncMonitoring,
  clearObjectCardData,
  closeFixedObjectCard,
  closeObjectCardOnMap,
  deleteMonitoringItem,
  loadMonitoring,
  loadMonitoringFailure,
  loadMonitoringSuccess,
  loadObjectCardData,
  loadObjectCardDataFailure,
  loadObjectCardDataSuccess,
  openObjectCardOnMap,
  pollMonitoringSuccess,
  setAllShowOnMap,
  setMaxRt,
  setMultipleShowOnMap,
  setShowOnMap,
  setTrackedObjectOnMapId,
  toggleFixedObjectCard,
  updateMultipleMonitoring,
  updateOneMonitoring,
} from '@store/monitoring/monitoring.actions';
import {
  initialMonitoringState,
  monitoringAdapter,
  MonitoringState,
} from '@store/monitoring/monitoring.state';

import { MonitoringItem } from '../../app/portal/pages/monitoring-page/monitoring.types';

export function getCoordinateKey(m: LastMessage): Partial<MonitoringItem> {
  const lat = m?.pos?.y;
  const lng = m?.pos?.x;

  if (typeof lat !== 'number' || typeof lng !== 'number') return {};

  const format = (value: number): string => Number(value.toFixed(ROUND_COORDINATE)).toString();

  const roundedLng = format(lng);
  const roundedLat = format(lat);

  const coordinateKey = `${roundedLng}_${roundedLat}`;

  return { coordinateKey };
}

export function withCoordinateKeyForItem(item: MonitoringItem): MonitoringItem {
  if (!item.lastMessage) return item; // нет сообщения — ничего не делаем

  return {
    ...item,
    ...getCoordinateKey(item.lastMessage),
  };
}

function setObjectCardLoading(
  state: MonitoringState,
  cardType: ObjectCardKind,
  objectId: number,
  loading: boolean,
): MonitoringState {
  const ids = state.objectCardsLoading[cardType];
  const isLoading = ids.includes(objectId);

  if (isLoading === loading) return state;

  return {
    ...state,
    objectCardsLoading: {
      ...state.objectCardsLoading,
      [cardType]: loading ? [...ids, objectId] : ids.filter((id) => id !== objectId),
    },
  };
}

/** Закрывает фиксированную карточку вместе с её данными — открытых экземпляров у неё больше нет */
function removeFixedObjectCard(state: MonitoringState, id: number): MonitoringState {
  const { [id]: removed, ...fixed } = state.objectCards.fixed;

  return {
    ...state,
    fixedObjectCardIds: state.fixedObjectCardIds.filter((cardId) => cardId !== id),
    objectCards: { ...state.objectCards, fixed },
  };
}

export const monitoringReducer = createReducer(
  initialMonitoringState,

  on(loadMonitoring, (state) => ({
    ...state,
    loading: true,
    error: undefined,
  })),

  on(loadMonitoringSuccess, (state, { monitoring }) => {
    const normalizedMonitoring = monitoring.map(withCoordinateKeyForItem);

    const nextVisibleIds = normalizedMonitoring.reduce<number[]>((acc, item) => {
      if (item.showOnMap) acc.push(item.id);
      return acc;
    }, []);

    return monitoringAdapter.setAll(normalizedMonitoring, {
      ...state,
      loading: false,
      visibleIds: nextVisibleIds,
    });
  }),

  on(loadMonitoringFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // --------------- update monitoring items ---------------

  on(addMonitoringItems, (state, { items }) => {
    const normalizedItems = items.map(withCoordinateKeyForItem);
    const updatedState = monitoringAdapter.addMany(normalizedItems, state);

    const newVisibleIds = normalizedItems.filter((item) => item.showOnMap).map((item) => item.id);

    const visibleIdsSet = new Set(state.visibleIds);
    newVisibleIds.forEach((id) => visibleIdsSet.add(id));

    return {
      ...updatedState,
      visibleIds: Array.from(visibleIdsSet),
    };
  }),
  on(addMonitoringItem, (state, { item }) => {
    const normalizedItem = withCoordinateKeyForItem(item);
    const updatedState = monitoringAdapter.addOne(normalizedItem, state);

    if (!normalizedItem.showOnMap) return updatedState;

    const visibleIdsSet = new Set(state.visibleIds);
    visibleIdsSet.add(normalizedItem.id);

    return {
      ...updatedState,
      visibleIds: Array.from(visibleIdsSet),
    };
  }),
  // ---------------------------------------------

  // --------------- update monitoring items ---------------

  on(updateOneMonitoring, (state, { id, changes }) =>
    monitoringAdapter.updateOne({ id, changes }, state),
  ),

  on(updateMultipleMonitoring, (state, { updates }) =>
    monitoringAdapter.updateMany(updates, state),
  ),
  // ---------------------------------------------

  on(deleteMonitoringItem, (state, { id }) => {
    const updatedState = monitoringAdapter.removeOne(id, state);

    const idAsNumber = +id;
    const isVisible = state.visibleIds.includes(idAsNumber);
    const { [idAsNumber]: removedFloating, ...floating } = state.objectCards.floating;
    const { [idAsNumber]: removedFixed, ...fixed } = state.objectCards.fixed;

    return {
      ...updatedState,
      visibleIds: isVisible
        ? state.visibleIds.filter((visibleId) => visibleId !== idAsNumber)
        : state.visibleIds,
      fixedObjectCardIds: state.fixedObjectCardIds.filter((cardId) => cardId !== idAsNumber),
      objectCards: { floating, fixed },
    };
  }),

  // --------------- Object cards ---------------
  on(loadObjectCardData, (state, { cardType, objectId }) =>
    setObjectCardLoading(state, cardType, objectId, true),
  ),

  on(loadObjectCardDataSuccess, (state, { cardType, objectCardInfo }) => ({
    ...setObjectCardLoading(state, cardType, objectCardInfo.id, false),
    objectCards: {
      ...state.objectCards,
      [cardType]: { ...state.objectCards[cardType], [objectCardInfo.id]: objectCardInfo },
    },
  })),

  on(loadObjectCardDataFailure, (state, { cardType, objectId }) =>
    setObjectCardLoading(state, cardType, objectId, false),
  ),

  on(clearObjectCardData, (state, { objectId, cardType }) => {
    if (!(objectId in state.objectCards[cardType])) return state;

    const { [objectId]: removed, ...rest } = state.objectCards[cardType];

    return { ...state, objectCards: { ...state.objectCards, [cardType]: rest } };
  }),

  // --------------- Fixed object cards ---------------
  on(toggleFixedObjectCard, (state, { id }) =>
    state.fixedObjectCardIds.includes(id)
      ? removeFixedObjectCard(state, id)
      : { ...state, fixedObjectCardIds: [...state.fixedObjectCardIds, id] },
  ),

  on(closeFixedObjectCard, (state, { id }) => removeFixedObjectCard(state, id)),
  // ---------------------------------------------

  // --------------- showOnMap ---------------

  on(setShowOnMap, (state, { id, show }) => {
    const updatedState = monitoringAdapter.updateOne({ id, changes: { showOnMap: show } }, state);

    const visibleIdsSet = new Set(state.visibleIds);
    show ? visibleIdsSet.add(id) : visibleIdsSet.delete(id);

    const newTrackedObjectOnMapId =
      state.trackedObjectOnMapId === id && !show ? null : state.trackedObjectOnMapId;

    return {
      ...updatedState,
      visibleIds: Array.from(visibleIdsSet),
      trackedObjectOnMapId: newTrackedObjectOnMapId,
    };
  }),

  on(setMultipleShowOnMap, (state, { updates }) => {
    const updateOperations = updates.map(({ id, show }) => ({
      id,
      changes: { showOnMap: show },
    }));

    const updatedState = monitoringAdapter.updateMany(updateOperations, state);
    const visibleIdsSet = new Set(state.visibleIds);

    for (const { id, show } of updates) {
      show ? visibleIdsSet.add(id) : visibleIdsSet.delete(id);
    }

    let newTrackedObjectOnMapId = updatedState.trackedObjectOnMapId;
    if (newTrackedObjectOnMapId !== null && !visibleIdsSet.has(newTrackedObjectOnMapId)) {
      newTrackedObjectOnMapId = null;
    }

    return {
      ...updatedState,
      visibleIds: Array.from(visibleIdsSet),
      trackedObjectOnMapId: newTrackedObjectOnMapId,
    };
  }),

  on(setAllShowOnMap, (state, { show }) => {
    const updateOperations = Object.values(state.entities)
      .filter((item): item is MonitoringItem => !!item)
      .map((item) => ({
        id: item.id,
        changes: { showOnMap: show },
      }));

    const updatedState = monitoringAdapter.updateMany(updateOperations, state);
    const updatedVisibleIds = show
      ? Object.keys(updatedState.entities).map((id) => Number(id))
      : [];

    return {
      ...updatedState,
      visibleIds: updatedVisibleIds,
    };
  }),

  on(openObjectCardOnMap, (state, { id }) => ({
    ...state,
    openedObjectCardOnMapId: id,
  })),

  on(closeObjectCardOnMap, (state) => ({
    ...state,
    openedObjectCardOnMapId: null,
  })),

  on(setTrackedObjectOnMapId, (state, { id }) => ({
    ...state,
    trackedObjectOnMapId: id,
  })),

  // ---------------------------------------------

  on(batchSyncMonitoring, (state, { items, deleteIds }) => {
    const normalizedItems = items.map(withCoordinateKeyForItem);
    const updatedState = monitoringAdapter.removeMany(deleteIds, state);
    const finalState = monitoringAdapter.upsertMany(normalizedItems, updatedState);

    const visibleIds = Object.values(finalState.entities)
      .filter((item): item is MonitoringItem => !!item && item.showOnMap)
      .map((item) => item.id);

    return {
      ...finalState,
      visibleIds,
    };
  }),

  on(setMaxRt, (state, { maxRt }) => ({ ...state, maxRt })),

  on(pollMonitoringSuccess, (state, { maxRt, lastInfo }) => {
    const updates = Object.entries(lastInfo).map(([idStr, info]) => {
      const id = Number(idStr);
      const changes: Partial<MonitoringItem> = {
        lastMessage: info.lastMessage ?? null,
        ...(info.lastMessage ? getCoordinateKey(info.lastMessage) : {}),
      };
      return { id, changes };
    });

    const updatedEntities = monitoringAdapter.updateMany(updates, state);

    return { ...updatedEntities, maxRt };
  }),
);
